import type { ReactNode } from 'react';
import {
  Bar,
  BarChart,
  LabelList,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
  type TooltipPayloadEntry,
  type TooltipValueType,
} from 'recharts';
import { calculateNiceTicks } from '../../../utils/chart';
import { formatCount, formatPercent } from '../../../utils/format';
import { TooltipContent } from './Tooltip';

type CustomProperties = Record<string, string | number>;

type BarItem = {
  label: string;
} & CustomProperties;

type LegendItem = {
  label: string;
  color?: string;
};

type GroupedDataItem = {
  label: string;
  data: {
    subLabel: string;
    value: number;
    color?: string;
  }[];
};

type Props = {
  groupedData: GroupedDataItem[];
  unit?: string;
  showsLegend?: boolean;
};

const DEFAULT_COLOR = '#023894';
const BAR_HEIGHT = 16;
const BAR_GAP = 12;
const X_LABEL_HEIGHT = 22 * 2;
const LEGEND_HEIGHT = 14;
const MAX_LABEL_LENGTH = 10;

const GroupedBarChart = ({
  groupedData,
  unit,
  showsLegend = true,
}: Props): ReactNode => {
  const barData = groupedData
    .map((item: GroupedDataItem): BarItem => {
      return {
        label: item.label,
        ...item.data.reduce(
          (properties: CustomProperties, data): CustomProperties => {
            return {
              ...properties,
              [data.subLabel]: data.value,
            };
          },
          {} as CustomProperties
        ),
      };
    })
    .reverse();

  const legends = groupedData
    .flatMap((item: GroupedDataItem) => {
      return item.data;
    })
    .reduce((legends: LegendItem[], data): LegendItem[] => {
      const exists = legends.find((legend) => legend.label === data.subLabel);
      return exists
        ? legends
        : [...legends, { label: data.subLabel, color: data.color }];
    }, [])
    .reverse();

  const totalBarCount = groupedData.length * (groupedData[0]?.data.length ?? 0);

  const sumValue = groupedData.reduce((total, item) => {
    return (
      total +
      item.data.reduce((subTotal, subItem) => subTotal + subItem.value, 0)
    );
  }, 0);

  const maxValue = Math.max(
    ...groupedData.map((item: GroupedDataItem): number => {
      return Math.max(
        ...item.data.map((data): number => {
          return data.value;
        })
      );
    })
  );

  return (
    <BarChart
      data={barData}
      dataKey="label"
      layout="vertical"
      barSize={BAR_HEIGHT}
      barCategoryGap={BAR_GAP}
      responsive
      width="100%"
      height={
        totalBarCount * (BAR_HEIGHT + BAR_GAP) + X_LABEL_HEIGHT + LEGEND_HEIGHT
      }
      margin={{
        right: unit ? unit.length * 6 + 42 : 8,
      }}
      style={{
        maxWidth: 600,
        margin: '0 auto',
      }}
    >
      <YAxis
        dataKey="label"
        type="category"
        tickFormatter={(label: string): string => {
          return label.length > MAX_LABEL_LENGTH
            ? `${label.slice(0, MAX_LABEL_LENGTH - 1)}…`
            : label;
        }}
        fontSize={12}
        width="auto"
      />
      <XAxis
        type="number"
        ticks={calculateNiceTicks('count', 0, maxValue)}
        label={
          unit
            ? {
                value: `[${unit}]`,
                position: 'right',
                dx: unit.length * 2 + 10,
                dy: -4,
                fontSize: 12,
              }
            : undefined
        }
        tickFormatter={(value: number): string => {
          return formatCount(value);
        }}
        fontSize={12}
      />
      <Tooltip
        contentStyle={{
          background: '#fffffff7',
          boxShadow: '4px 4px 15px 0 #0001',
          border: 'none',
          borderRadius: 3,
        }}
        itemStyle={{ paddingBlock: 2, fontSize: 12 }}
        labelStyle={{ fontSize: 12 }}
        formatter={(value: TooltipValueType | undefined): string => {
          return `${value} ${unit ?? ''}`;
        }}
        content={({ active, payload }: TooltipContentProps): ReactNode => {
          if (active && payload && payload.length > 0) {
            const data: BarItem = payload[0].payload;
            return (
              <TooltipContent
                label={data.label}
                values={payload.map(
                  (
                    item: TooltipPayloadEntry
                  ): { fill: string; value: string } => {
                    return {
                      fill: item.color || DEFAULT_COLOR,
                      value: `${item.name}: ${formatCount(Number(item.value))}${
                        unit && ` ${unit}`
                      }`,
                    };
                  }
                )}
              />
            );
          }
        }}
      />
      {showsLegend && (
        <Legend itemSorter={null} wrapperStyle={{ fontSize: 12 }} />
      )}
      {legends.map((legend: LegendItem): ReactNode => (
        <Bar
          key={legend.label}
          dataKey={legend.label}
          fill={legend.color || DEFAULT_COLOR}
          radius={BAR_HEIGHT * 0.25}
        >
          <LabelList
            dataKey={legend.label}
            position="right"
            fontSize={12}
            formatter={(value: unknown): string => {
              return typeof value === 'number' && sumValue > 0
                ? `${formatPercent((value / sumValue) * 100)}`
                : '';
            }}
          />
        </Bar>
      ))}
    </BarChart>
  );
};

export type { GroupedDataItem };
export { GroupedBarChart };
